'use client'
import React, { useMemo } from "react";
import Link from "next/link";
import { Wallet, LogOut, AlertTriangle, Gift } from "lucide-react";
import { useSelector } from "react-redux";
import Avatar from "../../common/Avatar";
import { useModal } from "../../Modals/ModalContext";
import { useAuthModal } from "../../../hooks/useAuthModal";

export default function UserProfileDropdown({
  userProfile,
  userDataFromLocal,
  walletAddress,
  formatAddress,
  connectedWallet,
  solBalance,
  isLoadingSolBalance,
  solBalanceError,
  selectedNetworkData,
  getInitials,
  setShowDisconnectWalletModal,
  setShowLogoutModal,
}) {
  const { bots = [], loading = "idle" } = useSelector(
    (state) => state.fetchBots || {}
  );
  const activeCount = useMemo(
    () => bots.filter((b) => b.status === "running").length,
    [bots]
  );
  const standbyCount = useMemo(
    () => bots.filter((b) => b.status === "stopped").length,
    [bots]
  );

  return (
    <div className="relative dropdown-container group">
      <div className="hover:scale-105 transition-all duration-200  cursor-pointer">
        <Avatar
          src={userDataFromLocal?.user?.avatar || userProfile?.avatar}
          name={userDataFromLocal?.user?.username || userDataFromLocal?.username || userProfile?.name}
          getInitials={getInitials}
          className="w-10 h-10"
        />
      </div>

      {/* Desktop Profile Dropdown - Shows on Hover.
          pt-3 keeps a clear gap under the header while remaining hoverable. */}
      <div className="absolute top-full right-0 z-[120] pt-5 opacity-0 invisible transition-all duration-200 group-hover:visible group-hover:opacity-100">
        <div className="w-80 overflow-hidden rounded-xl border border-gray-700 bg-gray-900 shadow-2xl">
        {/* Profile Header */}
        <div className="p-4 border-b border-gray-700/50 bg-gray-900">
          <div className="flex items-center space-x-3">
            <div>
              <Avatar
                src={userDataFromLocal?.user?.avatar}
                name={userDataFromLocal?.user?.username || userDataFromLocal?.username || userProfile?.name}
                getInitials={getInitials}
                size="lg"
              />
            </div>
            <div className="flex-1">
              <div className="text-white font-semibold">
                {userDataFromLocal?.user?.username || userDataFromLocal?.username || userProfile?.name || 'User'}
              </div>
              <div className="text-gray-400 text-sm">
                {userDataFromLocal?.user?.email || userDataFromLocal?.email || userProfile?.email || 'No email'}
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Info */}
        {walletAddress && <div className="p-3 border-b border-gray-700/50 bg-gray-900">
          <div className="text-gray-400 text-sm mb-2">Connected Wallet</div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-mono text-sm">
                {formatAddress(walletAddress)}
              </div>
              <div className="text-white font-semibold">
                {solBalanceError ? (
                  <div className="flex items-center space-x-1 text-red-400">
                    <AlertTriangle className="w-3 h-3" />

                    <span className="text-sm">Error{solBalanceError}</span>
                  </div>
                ) : (
                  `${solBalance.toFixed(6)} ${
                    selectedNetworkData?.symbol || "SOL"
                  }`
                )}
              </div>
            </div>
            <div className="text-xs text-gray-400">{connectedWallet}</div>
          </div>
        </div>}

        {/* Quick Stats */}
        {loading === 'loading' ? (
          <div className="p-4 border-b border-gray-700/50 bg-gray-900 rounded-lg animate-pulse">
            <div className="grid grid-cols-2 text-center gap-4">
              <div className="flex flex-col items-center space-y-2">
                <div className="h-6 w-10 bg-gray-700 rounded" />
                <div className="h-3 w-20 bg-gray-700 rounded" />
              </div>
              <div className="flex flex-col items-center space-y-2">
                <div className="h-6 w-10 bg-gray-700 rounded" />
                <div className="h-3 w-20 bg-gray-700 rounded" />
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 border-b border-gray-700/50 bg-gray-900 rounded-lg">
            <div className="grid grid-cols-2 gap-3">
              <Link
                href="/dashboard?tab=running"
                className="group block rounded-lg bg-gray-800/80 border border-gray-700 hover:border-gray-600 transition-colors p-3"
                rel="noopener noreferrer"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-300">Active bots</p>
                    <p className="text-2xl font-semibold text-white">
                      {activeCount}
                    </p>
                  </div>
                </div>
              </Link>

              <Link
                href="/dashboard?tab=standby"
                className="group block rounded-lg bg-gray-800/90 border border-gray-700 hover:border-gray-600 transition-colors p-3"
                rel="noopener noreferrer"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-300">Standby bots</p>
                    <p className="text-2xl font-semibold text-white">
                      {standbyCount}
                    </p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* Menu Actions */}
        <div className="p-3">
       
        {walletAddress && <button
          onClick={() => setShowDisconnectWalletModal(true)}
          className="w-full flex items-center justify-center space-x-2 p-2.5 bg-orange-500/20 hover:bg-orange-500/30 rounded-lg transition-colors text-orange-400 font-medium mb-2"
        >
          <Wallet className="w-4 h-4" />
          <span>Disconnect Wallet</span>
        </button>}

          {/* Logout Button */}
          <button
            onClick={() => setShowLogoutModal(true)}
            className="w-full flex items-center justify-center space-x-2 p-2.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors text-red-400 font-medium"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
